import { useQuery } from "@apollo/client";
import React, { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";

import { generateSHA256 } from "./crypto";
import { settingsQuery } from "./home/Home.gql";

interface AuthContextType {
    isAuthenticated: boolean;
    authenticate: (password: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({ isAuthenticated: false, authenticate: () => Promise.resolve(false) });

export function AuthProvider({ children }: PropsWithChildren<object>) {
    if (!window.isSecureContext) {
        console.error("Insecure context. App will not work");
    }

    // Auto-login from URL query parameter (e.g. from QR code)
    const authParam = new URLSearchParams(window.location.search).get("auth");
    if (authParam) {
        localStorage.setItem("password-hash", authParam);
        const url = new URL(window.location.href);
        url.searchParams.delete("auth");
        window.history.replaceState({}, "", url.pathname + url.hash);
    }

    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const { refetch } = useQuery(settingsQuery, {
        variables: {
            userId: "",
        },
        fetchPolicy: "network-only",
        onError: () => {
            setIsAuthenticated(false);
        },
        onCompleted: () => {
            setIsAuthenticated(true);
        },
    });
    const authenticate = useCallback<AuthContextType["authenticate"]>(
        async (password) => {
            try {
                const hash = await generateSHA256(password);
                localStorage.setItem("password-hash", hash);

                return await refetch().then((x) => !x.error);
            } catch (e) {
                console.error(e);
                return false;
            }
        },
        [refetch],
    );
    const value = useMemo<AuthContextType>(() => ({ isAuthenticated: !!isAuthenticated, authenticate }), [isAuthenticated, authenticate]);

    return <AuthContext.Provider value={value}>{isAuthenticated !== null ? children : null}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);

    return context;
}
